import React, { useEffect, useState } from "react";
import { FaFileCsv, FaSearch } from "react-icons/fa";
import DateRangePicker from "../components/DateRangePicker";
import { AnalyticEvent } from "../models/AnalyticEvent";
import {
  convertLocalDateStrToUtc,
  convertToLocalDateTime,
} from "../utilities/DateUtil";
import {
  exportAnalyticEventsCsv,
  searchAnalyticEvents,
} from "../services/AnalyticEventService";
import {
  Button,
  Center,
  Flex,
  Group,
  Loader,
  NumberInput,
  Pagination,
  Paper,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from "@mantine/core";

const AppAnalytics = () => {
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticEvent[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [pageNum, setPageNum] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalNumber, setTotalNumber] = useState<number>(0);
  const [searched, setSearched] = useState<boolean>(false);

  const theme = useMantineTheme();

  const onDateRangeSelected = (startDate?: string, endDate?: string) => {
    setStartDate(startDate ? convertLocalDateStrToUtc(startDate) : null);
    setEndDate(endDate ? convertLocalDateStrToUtc(endDate, true) : null);
  };

  const onSearch = async () => {
    if (startDate && endDate) {
      setLoading(true);

      try {
        const analytics = await searchAnalyticEvents(
          pageNum,
          startDate,
          endDate,
          setTotalPages,
          setTotalNumber,
          setLoading,
        );
        setAnalytics(analytics);
        if (!searched) {
          setSearched(true);
        }
      } catch (error) {
        console.error("Error fetching analytics:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const onExportToCsv = async () => {
    if (searched && startDate && endDate) {
      setLoading(true);

      await exportAnalyticEventsCsv(startDate, endDate, setLoading);
    }
  };

  useEffect(() => {
    if (startDate && endDate && searched) {
      onSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNum]);

  useEffect(() => {
    if (!startDate || !endDate) {
      setSearched(false);
    }
  }, [startDate, endDate]);

  return (
    <Flex direction="column" w="100%">
      <Paper shadow="xl" p="md" my="md">
        <Group justify="center">
          <DateRangePicker onChange={onDateRangeSelected} />
          <Button
            onClick={onSearch}
            size="md"
            disabled={!startDate || !endDate}
            leftSection={<FaSearch className="search-icon" />}
            title={
              !startDate || !endDate
                ? "Select both a start date and an end date to enable searching."
                : ""
            }
          >
            Search
          </Button>
          {analytics && analytics.length > 0 && (
            <Button
              size="md"
              leftSection={<FaFileCsv size={20} color="white" />}
              onClick={onExportToCsv}
              disabled={!searched}
            >
              Export
            </Button>
          )}
        </Group>
      </Paper>

      {startDate && endDate && searched && (
        <Paper p="md" shadow="xl">
          <Group justify="center" gap="md">
            <Title order={4}>Page</Title>

            <Pagination
              total={totalPages}
              value={pageNum}
              onChange={setPageNum}
              withEdges
            />
            <NumberInput
              styles={{ input: { width: "100px" } }}
              min={1}
              max={totalPages}
              value={pageNum}
              onChange={(value) => {
                if (value >= 1 && value <= totalPages) {
                  setPageNum(value as number);
                }
              }}
            />
            <Text>
              Total {totalPages} pages; {totalNumber} analytic events in current
              page
            </Text>
          </Group>
        </Paper>
      )}

      <Paper p="md" shadow="xl" mb="md">
        <Group
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr 1fr",
          }}
        >
          <Text span fw={700}>
            Event Trigger
          </Text>
          <Text span fw={700}>
            Event Name
          </Text>
          <Text span fw={700}>
            Event Value
          </Text>
          <Text span fw={700}>
            App Name
          </Text>
          <Text span fw={700}>
            Version
          </Text>
          <Text span fw={700}>
            User Id
          </Text>
          <Text span fw={700}>
            Create Time
          </Text>
        </Group>
      </Paper>

      {loading ? (
        <Center mt="xl">
          <Loader size="xl" />
        </Center>
      ) : (
        <Stack gap="xs">
          {startDate && endDate && searched ? (
            analytics.map((analytic, index) => (
              <Paper key={index} p="md" shadow="xl">
                <Group
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr 1fr",
                  }}
                >
                  <Text span>{analytic.eventTrigger}</Text>
                  <Text span>{analytic.eventName}</Text>
                  <Text span>{analytic.eventValue}</Text>
                  <Text span>{analytic.appName}</Text>
                  <Text span>{analytic.deviceVersion}</Text>
                  <Text span>{analytic.userId}</Text>
                  <Text span>{convertToLocalDateTime(analytic.createdAt)}</Text>
                </Group>
              </Paper>
            ))
          ) : (
            <Center mt="lg">
              <Text c={theme.colors.gray[7]}>
                Set date range to begin search!
              </Text>
            </Center>
          )}
        </Stack>
      )}
    </Flex>
  );
};

export default AppAnalytics;
